import React, { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { WebService } from "../../services/WebService";
import { toast } from "react-toastify";
import { HiDotsVertical } from "react-icons/hi";
import Button from "@mui/material/Button";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import useModalCloseHook from "../../hooks/useModalCloseHook";
import { useChatNotifyCount } from "../../store/ChatNotifyCount";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ADMIN, PROJECT_COORDINATOR } from "../../constants/roles";

const ChatNotification = ({ setIsNotification }) => {
  const modalRef = useRef(null);
  const actionRef = useRef(null);
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth?.user);
  const [notifications, setNotifications] = useState([]);
  const [totalPages, setTotalpages] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPerformAction, setIsPerformAction] = useState(null);

  const { removeAllChatNotifyCount, decreaseChatNotifyCount } =
    useChatNotifyCount();

  const handleClose = () => setIsNotification(false);
  useModalCloseHook(modalRef, handleClose);

  const getShortName = (msg) => {
    const keyword = "sends";
    const index = msg.indexOf(keyword);
    if (index === -1) return "";
    const name = msg.substring(0, index).trim().split(" ");
    return (
      (name[0]?.charAt(0).toUpperCase() || "") +
      (name.length > 1
        ? name[name.length - 1]?.charAt(0).toUpperCase()
        : "")
    );
  };

  const handleNavigate = (project) => {
    if (user.role === ADMIN) {
      navigate(`/view-project/${project.id}/chat`, {
        state: { projectName: project.projectName },
      });
    } else if (user.role === PROJECT_COORDINATOR) {
      navigate(`/emp-projects-details/${project.id}/chat`, {
        state: { projectName: project.projectName },
      });
    } else {
      navigate(`/client-project-details/${project.id}/chat`, {
        state: { projectName: project.projectName },
      });
    }
  };

  const handleNotifyCount = async (id, isRead) => {
    if (isRead) return;
    try {
      const response = await WebService.markNotificationAsRead(id);
      if (response) {
        decreaseChatNotifyCount();
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleremoveAllChatNotifyCount = async () => {
    if (!notifications.length) return;
    try {
      const response = await WebService.markAllNotificationAsRead();
      if (response) {
        removeAllChatNotifyCount();
        setNotifications([]);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteNotification = async (id, isRead) => {
    try {
      const response = await WebService.deleteNotification(id);
      if (response) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        if (!isRead) decreaseChatNotifyCount();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const fetchNotification = async () => {
      try {
        setLoading(true);
        const message = await WebService.getNotification(
          currentPage,
          10,
          date,
          "message"
        );
        if (message) {
          console.log(message.data);
          
          setNotifications(message.data);
          setTotalpages(message.pagination.totalPages);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchNotification();
  }, [currentPage, date]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionRef.current && !actionRef.current.contains(e.target)) {
        setIsPerformAction(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <AnimatePresence>
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="
            absolute right-0 sm:right-2 mt-16
            w-full sm:w-[450px] xl:w-[550px]
            bg-white rounded-xl shadow-2xl z-50
          "
        >
          {/* Header */}
          <div className="flex flex-wrap sm:flex-nowrap justify-between gap-2 px-4 py-2">
            <h3 className="text-lg font-semibold text-gray-800">
              Chat Notifications
            </h3>

            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="outline-none border border-zinc-400 rounded px-2 text-sm"
              />

              <Button
                variant="outlined"
                onClick={handleremoveAllChatNotifyCount}
                className="!text-sm !capitalize text-blue-600 border border-blue-600"
              >
                Mark all as read
              </Button>

              <button onClick={handleClose}>
                <XIcon className="w-6 h-6 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[70vh] overflow-y-auto divide-y">
            {loading ? (
              <p className="py-10 text-center text-primary">Loading...</p>
            ) : notifications.length === 0 ? (
              <p className="py-10 text-center text-gray-400">
                No chat notifications available.
              </p>
            ) : (
              notifications.map((note) => (
                <div
                  key={note.id}
                  onClick={() => handleNavigate(note.project)}
                  className={`cursor-pointer flex gap-3 px-4 py-2 hover:bg-gray-200 ${
                    !note.isRead ? "bg-gray-100" : ""
                  }`}
                >
                  <div className="h-10 w-10 shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-sm">
                    {getShortName(note.message)}
                  </div>

                  <div className="flex-1">
                    <p className="text-sm text-gray-800 line-clamp-2">
                      {note.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(note.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div
                    className="relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <HiDotsVertical
                      onClick={() =>
                        setIsPerformAction(
                          isPerformAction === note.id ? null : note.id
                        )
                      }
                      className="w-4 h-4 text-gray-500 cursor-pointer"
                    />

                    {isPerformAction === note.id && (
                      <div
                        ref={actionRef}
                        className="absolute top-6 right-0 w-[160px] bg-white shadow-lg border rounded-md z-10"
                      >
                        <button
                          disabled={note.isRead}
                          onClick={() =>
                            handleNotifyCount(note.id, note.isRead)
                          }
                          className="w-full px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          Mark as Read
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteNotification(note.id, note.isRead)
                          }
                          className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {notifications.length > 0 && (
            <div className="flex justify-end items-center gap-2 px-4 py-2">
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-primary/10 disabled:bg-gray-200"
              >
                <MdKeyboardArrowLeft />
              </button>

              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-primary/10 disabled:bg-gray-200"
              >
                <MdKeyboardArrowRight />
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ChatNotification;
